/**
 * WebSocket Service
 *
 * Socket.io client for real-time updates
 * Connects to WebSocket server and handles authentication.
 * Updates websocketStore on connection lifecycle events.
 */

import { io, Socket } from 'socket.io-client';
import { useAuthStore } from '../store/authStore';
import { useWebSocketStore } from '../store/websocketStore';
import { config } from '../config/runtime';

class SocketService {
  private socket: Socket | null = null;
  private readonly WS_URL = config.WS_URL;

  /**
   * Connect to WebSocket server with authentication
   */
  connect(): Socket {
    if (this.socket?.connected) {
      return this.socket;
    }

    const token = useAuthStore.getState().token;
    if (!token) {
      throw new Error('Cannot connect to WebSocket: No authentication token');
    }

    this.socket = io(this.WS_URL, {
      auth: {
        token
      },
      transports: ['websocket'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 60000,
      reconnectionAttempts: 10,
      timeout: 10000,
    });

    this.socket.on('connect', () => {
      useWebSocketStore.getState().setStatus('connected');
      useWebSocketStore.getState().setReconnectAttempt(0);
      if (import.meta.env.DEV) {
        console.log('WebSocket connected:', this.socket?.id);
      }
    });

    this.socket.on('disconnect', (reason) => {
      // If the server disconnected us or transport closed, we expect reconnection
      // to happen automatically. Mark as reconnecting unless it was a clean close.
      if (reason === 'io client disconnect') {
        useWebSocketStore.getState().setStatus('disconnected');
      } else {
        useWebSocketStore.getState().setStatus('reconnecting');
      }
      if (import.meta.env.DEV) {
        console.log('WebSocket disconnected:', reason);
      }
    });

    this.socket.on('connect_error', () => {
      // Silently handle — WebSocket server may not be running in demo mode
      // Status will be updated by reconnect_attempt or reconnect_failed
    });

    this.socket.io.on('reconnect_attempt', (attempt: number) => {
      useWebSocketStore.getState().setStatus('reconnecting');
      useWebSocketStore.getState().setReconnectAttempt(attempt);
      if (import.meta.env.DEV) {
        console.log(`WebSocket reconnect attempt ${attempt}`);
      }
    });

    this.socket.io.on('reconnect', (attempt: number) => {
      useWebSocketStore.getState().setStatus('connected');
      useWebSocketStore.getState().setReconnectAttempt(0);
      if (import.meta.env.DEV) {
        console.log(`WebSocket reconnected after ${attempt} attempts`);
      }
    });

    this.socket.io.on('reconnect_failed', () => {
      useWebSocketStore.getState().setStatus('disconnected');
      if (import.meta.env.DEV) {
        console.log('WebSocket reconnection failed after max attempts');
      }
    });

    this.socket.on('welcome', () => {
      // Connection established
    });

    return this.socket;
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect(): void {
    if (this.socket) {
      if (import.meta.env.DEV) {
        console.log('WebSocket disconnecting...');
      }
      this.socket.disconnect();
      this.socket = null;
      useWebSocketStore.getState().reset();
    }
  }

  /**
   * Attempt to reconnect (used when user clicks reconnect)
   */
  reconnect(): void {
    if (this.socket) {
      this.socket.connect();
    } else {
      // Socket was fully torn down — create fresh connection
      this.connect();
    }
  }

  /**
   * Get current socket instance
   */
  getSocket(): Socket | null {
    return this.socket;
  }

  /**
   * Check if socket is connected
   */
  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }
}

// Export singleton instance
export const socketService = new SocketService();

// Export socket getter for convenience
export const getSocket = () => socketService.getSocket();
